import { CopyTwoTone } from "@ant-design/icons";
import { Button, Input, Spacing } from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import copy from "copy-to-clipboard";
import { useCallback, useMemo, useState } from "react";

/** Renders textual commands to the user
 *
 * The component renders commands in a non-editable multiline input,
 * with an optional button to copy the commands to the clipboard.
 */
export const CommandDisplay: React.FC<{
  /** The command string to display */
  commands: string;
  /** If true, will not render the copy button */
  hideCopy?: boolean;
  /** If passed, sets the maximum number of displayed rows (default 40) */
  maxRows?: number;
  /** If passed, sets the minimum number of displayed rows (default 2) */
  minRows?: number;
}> = ({ commands, hideCopy, minRows, maxRows }) => {
  const onCopy = useCallback(() => copy(commands), [commands]);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const text = useMemo(() => commands?.trim() ?? "", [commands]);
  const textRows = useMemo(() => text.split("\n").length, [text]);
  const concreteMinRows = minRows ?? (hideCopy ? 0 : 2);
  const concreteMaxRows = maxRows ?? 40;

  const handleCopyClick = useCallback(() => {
    onCopy();
    setTooltipVisible(true);
    setTimeout(() => {
      setTooltipVisible(false);
    }, 1000);
  }, [onCopy]);

  return (
    <Spacing asChild mb="0.8em">
      <div
        style={{
          position: "relative",
        }}
      >
        {!hideCopy && (
          <GraphTooltip
            title="Copied"
            trigger="click"
            color={"blue"}
            open={tooltipVisible}
          >
            <Button
              /* eslint-disable-line react/forbid-component-props */ style={{
                position: "absolute",
                right:
                  textRows > concreteMaxRows
                    ? "1.5em" // room for scroll bar
                    : "0.5em",
                top: "0.5em",
                zIndex: 2,
              }}
              icon={<CopyTwoTone />}
              onClick={handleCopyClick}
            />
          </GraphTooltip>
        )}
        <Input.TextArea
          value={text}
          autoSize={{
            minRows: concreteMinRows,
            maxRows: concreteMaxRows,
          }}
          styles={{
            textarea: {
              background: "rgba(150,150,150, 0.1)",
              fontSize: 12,
              fontFamily: `Monaco, Consolas, Menlo, "Courier New", monospace`,
              marginBottom: 0,
              paddingRight: hideCopy ? 0 : "42px",
            },
          }}
          readOnly
        />
      </div>
    </Spacing>
  );
};
